import { useState } from "react";
import { motion } from "framer-motion";
import { X, Check } from "lucide-react";
import { PERSONAS } from "@/data/content";
import { MaskLines, Reveal } from "@/components/site/Reveal";

export const PersonaTabs = () => {
  const [active, setActive] = useState(PERSONAS[0].id);
  const p = PERSONAS.find((x) => x.id === active);

  return (
    <section data-testid="persona-section" className="bg-[#FAFCFF] py-24 sm:py-32">
      <div className="container-x">
        <div className="flex flex-col gap-10 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="eyebrow text-[#1777AF]">Who we help</p>
            <MaskLines
              lines={["You run the business.", { text: "We handle the tech.", className: "text-slate-400" }]}
              className="mt-5 font-display text-4xl font-bold leading-[1.02] tracking-tight text-slate-900 sm:text-5xl lg:text-6xl"
            />
          </div>
          <Reveal className="shrink-0">
            <div role="tablist" className="flex flex-col gap-1 rounded-3xl border border-slate-200 bg-white p-1.5 sm:flex-row sm:rounded-full">
              {PERSONAS.map((x) => (
                <button
                  key={x.id}
                  role="tab"
                  aria-selected={active === x.id}
                  data-testid={`persona-tab-${x.id}`}
                  onClick={() => setActive(x.id)}
                  className={`relative flex-1 whitespace-nowrap rounded-full px-4 py-2.5 text-sm font-semibold transition-colors duration-300 ${active === x.id ? "text-white" : "text-slate-600 hover:text-slate-900"}`}
                >
                  {active === x.id && <motion.span layoutId="persona-pill" className="absolute inset-0 rounded-full bg-[#0E2657]" transition={{ type: "spring", stiffness: 300, damping: 30 }} />}
                  <span className="relative">{x.label}</span>
                </button>
              ))}
            </div>
          </Reveal>
        </div>

        <motion.div
          key={p.id}
          data-testid="persona-panel"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
          className="mt-14 grid gap-px overflow-hidden rounded-3xl border border-slate-200 bg-slate-200 lg:grid-cols-12"
        >
          <div className="bg-white p-8 sm:p-10 lg:col-span-4">
            <p className="eyebrow text-slate-500">What keeps you up</p>
            <ul className="mt-6 space-y-4">
              {p.pains.map((t) => (
                <li key={t} className="flex items-start gap-3 text-slate-700">
                  <X className="mt-0.5 h-5 w-5 shrink-0 text-rose-500" /> {t}
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-white p-8 sm:p-10 lg:col-span-5">
            <p className="eyebrow text-[#1777AF]">How Bodhatek helps</p>
            <h3 className="mt-6 font-display text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">{p.title}</h3>
            <p className="mt-4 flex items-start gap-3 leading-relaxed text-slate-600">
              <Check className="mt-1 h-5 w-5 shrink-0 text-emerald-500" /> {p.help}
            </p>
          </div>
          <div className="relative flex flex-col justify-end overflow-hidden bg-[#0E2657] p-8 sm:p-10 lg:col-span-3">
            <div className="absolute -right-16 -top-16 h-48 w-48 rounded-full bg-[#29C1D3]/30 blur-3xl" />
            <p data-testid="persona-metric" className="relative font-display text-5xl font-bold text-grad sm:text-6xl">{p.metric.value}</p>
            <p className="relative mt-3 text-sm text-slate-300">{p.metric.label}</p>
          </div>
        </motion.div>
        <p className="mt-4 text-xs text-slate-500">*Illustrative placeholder metrics.</p>
      </div>
    </section>
  );
};
